import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Header2,
} from "../../../components/ui";
import { PasswordInput, TextInput } from "../../../components/ui/inputs";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "../services/auth.service";
import { useEffect, useState } from "react";
import { localStorageService } from "../../../services";
import { LoginResponse } from "../models";
import { formatUtcToLocalDateTime } from "../../../utils/dateTimeHelper";
import common from "../../../utils/common";
import localization from "../../../config/localization";
import { RegistrationType } from "../../../models/constants/registrationType";
import { useAppDispatch } from "../../../store/hooks";
import { changeLoaderState, changeLoginModalState } from "../../../reducers/appStateReducer";
import { fetchUserProfile } from "../../users/actions/userProfileActions";
import {
  RESET_PASSWORD_TYPE_WHATSAPP,
  RESET_PASSWORD_TYPES,
} from "../models/resetPasswordType";
import { DeviceType } from "../../../models/constants/deviceType";
import homeService from "../../home/services/home.service";
import { BannerImageResponse } from "../../home/models";
import userDeviceStore from "../../../store/device.store";
import errorModal from "../../../services/errorModal.service";
import { CommonConfigsResponse } from "../../../models/Response/commonConfigResponse";
import { commonService } from "../../../services/common.service";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const from = (location.state as any)?.from?.pathname || "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [banners, setBanners] = useState<BannerImageResponse>();
  const [config, setConfig] = useState<CommonConfigsResponse | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [UsernameError, setUsernameError] = useState<string | null>(null);
  const [PasswordError, setPasswordError] = useState<string | null>(null);

  const deviceType = userDeviceStore((state) => state.deviceType);

  useEffect(() => {
    const fetchCommonConfig = async () => {
      const response = await commonService.fetchCommonConfig();
      if (response.isSuccess) {
        setConfig(response.data);
      }
    };
    fetchCommonConfig();
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      const response = await homeService.retrieveBanners({
        bannerType: "Login",
        deviceType: deviceType,
        language: "English",
      });

      if (response.isSuccess) {
        setBanners(response.data ?? undefined);
      }
    };

    fetchBanners();
  }, [deviceType]);

  useEffect(() => {
    setContentHeight(document.getElementById("login-content")?.clientHeight ?? 0);
  }, []);

  const replaceUtcDateTimeWithLocal = (message?: string): string => {
    if (!message) return "";

    const datetimePattern = /\b\d{2}-\d{2}-\d{4} at \d{2}:\d{2}\b/;
    const match = message.match(datetimePattern);

    if (!match) return message;

    const datetimeString = match[0];

    const isoLike = datetimeString
      .replace(" at ", " ")
      .replace(/(\d{2})-(\d{2})-(\d{4})/, "$3-$2-$1");

    const date = new Date(isoLike);

    if (isNaN(date.getTime())) return message;

    const localDateTime = formatUtcToLocalDateTime(date, false);

    return message.replace(datetimeString, localDateTime);
  };

  const clearUsernameErrorOnFocus = () => {
    setUsernameError((prev) => (prev ? null : prev));
  };

  const clearPasswordErrorOnFocus = () => {
    setPasswordError((prev) => (prev ? null : prev));
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setUsernameError((prev) => (value ? null : prev));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError((prev) => (value ? null : prev));
  };

  const checkValidation = () => {
    let isValid = true;

    setUsernameError(null);
    setPasswordError(null);

    if (!username) {
      setUsernameError("error_Username");
      isValid = false;
    }
    if (!password) {
      setPasswordError("error_Password");
      isValid = false;
    }
    return isValid;
  };

  const handleLogin = async (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!checkValidation()) {
      return;
    }

    const response = await authService.login({
      browser: common.detectBrowser(),
      operatingSystem: common.detectOperatingSystem(),
      resolution: common.detectScreenResolution(),
      browserVersion: common.detectBrowserVersion(),
      device: common.detectDeviceType(),
      ipAddress: config?.ip ?? "",
      username: username,
      password: password,
      accessMode: userDeviceStore.getState().accessModeSource.sourceType,
    });

    if (response.isSuccess) {
      dispatch(changeLoaderState(true));
      dispatch(fetchUserProfile());

      const data = response.data as LoginResponse;

      localStorageService.setItem("token", data.token);
      localStorageService.setItem("refreshToken", data.refreshToken);
      localStorageService.setItem("username", data.username);
      localStorageService.setItem("providerUserId", data.providerUserId);
      localStorageService.setItem("userId", data.id);
      localStorageService.setItem("isUserLoggedIn", true);

      if (
        ((data.registrationType === RegistrationType.ManualOrWhatsApp &&
          data.isOneTimePasswordChanged) ||
          data.registrationType) &&
        !data.isSecondLoginDone &&
        data.isSecondLoginAttempt &&
        (!data.emailVerified || !data.mobileVerified)
      ) {
        dispatch(changeLoaderState(false));
        navigate("/user-verification");
      } else if (
        data.registrationType &&
        data.registrationType.toLowerCase() ===
        RegistrationType.ManualOrWhatsApp.toLowerCase() &&
        data.isOneClickRegistration &&
        !data.isOneTimePasswordChanged
      ) {

        window.location.reload();
        setTimeout(() => {
          navigate("/reset-password", {
            state: {
              showBackButton: false,
              resetPasswordType: RESET_PASSWORD_TYPES.find(
                (t) => t.type === RESET_PASSWORD_TYPE_WHATSAPP
              ),
            },
          });
        }, 5000);
        dispatch(changeLoaderState(false));
      } else {
        window.location.href = from;
        dispatch(changeLoginModalState(true));
        setTimeout(() => {
          dispatch(changeLoaderState(false));
        }, 1000);
      }
    } else {
      const formattedMessage = replaceUtcDateTimeWithLocal(response.message);
      errorModal.showErrorModal(response.statusCode, formattedMessage, false);
    }
  };

  const shouldShowBanner =
    deviceType === DeviceType.Web &&
    banners?.details &&
    banners.details.length > 0;

  return (
    <Card>
      {deviceType === DeviceType.Mobile && (
        <CardHeader
          showBackButton={true}
          showLogo={true}
          isRedirectedFromLogin={true}
        />
      )}

      <CardBody showGradient={true}>
        <div
          className={
            shouldShowBanner
              ? "grid grid-cols-1 md:grid-cols-2 min-h-[500px]"
              : "flex justify-center items-center min-h-[500px]"
          }
        >
          {shouldShowBanner && (
            <div className="hidden md:block h-full pr-10">
              <img
                src={`data:image/png;base64,${banners?.details?.[0]?.banner}`}
                alt="Banner"
                className="w-full h-full object-cover"
                style={{ height: contentHeight }}
              />
            </div>
          )}

          <div
            id="login-content"
            className={
              shouldShowBanner
                ? "pt-5"
                : "w-full max-w-xl"
            }
          >
            <Header2 className="text-white mb-6">
              {localization.welcome_back}
            </Header2>

            <form
              className="flex flex-col gap-3"
              onSubmit={handleLogin}
            >
              <TextInput
                label={localization.username}
                type="text"
                placeholder={localization.username}
                value={username}
                onChange={handleUsernameChange}
                onFocus={clearUsernameErrorOnFocus}
                error={UsernameError && localization.getString(UsernameError)}
                maxLength={15}
              />

              <div>
                <PasswordInput
                  label={localization.password}
                  placeholder={localization.password}
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={clearPasswordErrorOnFocus}
                  error={PasswordError && localization.getString(PasswordError)}
                  maxLength={20}
                />

                <div className="flex justify-end">
                  <label
                    className="text-white hover:cursor-pointer hover:underline"
                    onClick={() => navigate("/forgot-password")}
                  >
                    {localization.forgot_password}
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                className="mt-2 w-full py-4 rounded-md text-white bg-primary-gradient flex justify-center"
              >
                <span className="uppercase">{localization.login}</span>
              </Button>
            </form>

            <div className="flex justify-center py-2">
              <div
                className="gap-2 text-white text-m mt-2 cursor-pointer w-fit"
                onClick={() => navigate("/signup")}
              >
                {localization.dont_have_an_account} {" "}
                <span>{localization.signup}</span>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}